import { computed, effect, inject, Injectable } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';

import { Caractéristique } from '../../../référentiel-métier/référentiel-métier-périmètre/perimetre-caracteristiques/models/caractéristique.model';
import { SidepanelService } from '../../../shared/components/sidepanel/sidepanel.service';
import { FormControlNames, FormGroupNames } from '../../models/form-control-names.model';
import { JalonStore } from '../../store/jalon/jalon.store';

@Injectable({
  providedIn: 'root',
})
export class JalonDetailFormService {
  readonly #fb = inject(FormBuilder);
  readonly #jalonStore = inject(JalonStore);
  readonly #sidepanelService = inject(SidepanelService);

  jalonSelectionne = computed(() => this.#jalonStore.selectedEntity());

  form: FormGroup = this.#fb.group({
    [FormGroupNames.GENERAL]: this.#fb.group({
      [FormControlNames.NOM]: [{ value: null, disabled: true }],
      [FormControlNames.NIVEAU_CEC]: [null, [Validators.required, Validators.min(3), Validators.max(8)]],
    }),
    [FormGroupNames.COMPAGNONNAGE]: this.#fb.group({
      [FormControlNames.COMPETENCES_COMPAGNONNIQUES]: [[]],
    }),
    [FormGroupNames.METIER]: this.#fb.group({
      [FormControlNames.SECTEURS_ACTIVITE]: [[]],
      [FormControlNames.EMPLOIS_POSSIBLES]: [{ value: null, disabled: true }],
      [FormControlNames.COMPETENCES_TRANSVERSES]: [{ value: null, disabled: true }],
    }),
    [FormGroupNames.SCIENCES_ET_CULTURE]: this.#fb.group({
      [FormControlNames.SAVOIRS_SCIENTIFIQUES_CULTUREL]: [{ value: null, disabled: true }],
    }),
  });

  constructor() {
    effect(() => {
      const jalon = this.jalonSelectionne();
      if (jalon) {
        this.form.setValue(
          {
            [FormGroupNames.GENERAL]: {
              [FormControlNames.NOM]: jalon.nom,
              [FormControlNames.NIVEAU_CEC]: jalon.niveauCec ?? null,
            },
            [FormGroupNames.COMPAGNONNAGE]: {
              [FormControlNames.COMPETENCES_COMPAGNONNIQUES]: [],
            },
            [FormGroupNames.METIER]: {
              [FormControlNames.SECTEURS_ACTIVITE]: jalon.secteursActivite,
              [FormControlNames.EMPLOIS_POSSIBLES]: [],
              [FormControlNames.COMPETENCES_TRANSVERSES]: [],
            },
            [FormGroupNames.SCIENCES_ET_CULTURE]: {
              [FormControlNames.SAVOIRS_SCIENTIFIQUES_CULTUREL]: [],
            },
          },
          { emitEvent: false },
        );
      }
    });
  }

  submit() {
    const idJalonSelectionne = this.jalonSelectionne()?.id;

    if (!idJalonSelectionne) return;

    this.#sidepanelService.loading.set(true);

    this.#jalonStore.modifier({
      idJalon: idJalonSelectionne,
      data: {
        nom: this.nom.value,
        niveauCec: this.niveauCec.value,
        idsSecteursActivite: this.secteursActivite.value?.map((x) => x.id),
      },
      next: () => this.#sidepanelService.loading.set(false),
    });
  }

  get general(): FormGroup {
    return this.form.get(FormGroupNames.GENERAL) as FormGroup;
  }

  get compagnonnage(): FormGroup {
    return this.form.get(FormGroupNames.COMPAGNONNAGE) as FormGroup;
  }

  get metier(): FormGroup {
    return this.form.get(FormGroupNames.METIER) as FormGroup;
  }

  get sciencesCulture(): FormGroup {
    return this.form.get(FormGroupNames.SCIENCES_ET_CULTURE) as FormGroup;
  }

  // Général
  get nom(): AbstractControl<string> {
    return this.general.get(FormControlNames.NOM) as FormControl<string>;
  }
  get niveauCec(): AbstractControl<number> {
    return this.general.get(FormControlNames.NIVEAU_CEC) as FormControl<number>;
  }

  // Compagnonnage
  get competencesCompagnonniques(): AbstractControl<any[]> {
    return this.compagnonnage.get(FormControlNames.COMPETENCES_COMPAGNONNIQUES) as FormControl<any[]>;
  }

  // Métier
  get secteursActivite() {
    return this.metier.get(FormControlNames.SECTEURS_ACTIVITE) as FormControl<Caractéristique[]>;
  }
  get emploisPossibles(): AbstractControl<string> {
    return this.metier.get(FormControlNames.EMPLOIS_POSSIBLES) as FormControl<string>;
  }
  get competencesTransverses(): AbstractControl<string> {
    return this.metier.get(FormControlNames.COMPETENCES_TRANSVERSES) as FormControl<string>;
  }

  // Sciences et culture
  get savoirs(): AbstractControl<string> {
    return this.sciencesCulture.get(FormControlNames.SAVOIRS_SCIENTIFIQUES_CULTUREL) as FormControl<string>;
  }
}
